/**
 * LagAttributionTooltip — shared tooltip for the "Why did THIS feel laggy?"
 * hover. Consumed by AbilityLifecycleMonitor (React right-click) and the
 * in-game canvas contextmenu listener.
 */
import React from 'react';
import type { CastTimingBreakdown } from '../hooks/useCastLifecycleStream';

interface Props {
  breakdown: CastTimingBreakdown;
  abilityName?: string;
  style?: React.CSSProperties;
  /** Render as a single compact line instead of the full multi-line block. */
  compact?: boolean;
}

function timingColor(ms: number): string {
  if (ms < 50) return '#51cf66';
  if (ms < 150) return '#ffd23f';
  return '#ff5f5f';
}

export const LagAttributionTooltip: React.FC<Props> = ({ breakdown, abilityName, style, compact }) => {
  if (compact) {
    return (
      <div
        style={{
          position: 'fixed',
          zIndex: 10000,
          background: 'rgba(10, 12, 20, 0.95)',
          border: '1px solid #2a2f3a',
          borderRadius: 4,
          padding: '4px 10px',
          fontFamily: 'monospace',
          fontSize: 11,
          color: '#ecf0f1',
          boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          ...style,
        }}
      >
        {formatAttributionString(breakdown, abilityName)}
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        zIndex: 10000,
        background: 'rgba(10, 12, 20, 0.95)',
        border: '1px solid #2a2f3a',
        borderRadius: 4,
        padding: '6px 10px',
        fontFamily: 'monospace',
        fontSize: 11,
        color: '#ecf0f1',
        boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
        pointerEvents: 'none',
        maxWidth: 260,
        ...style,
      }}
    >
      {abilityName && (
        <div style={{ color: '#7af', marginBottom: 4, fontWeight: 'bold' }}>{abilityName}</div>
      )}
      <div style={{ color: '#aaa', marginBottom: 4 }}>Why did this feel laggy?</div>
      <div style={{ lineHeight: 1.6 }}>
        <div>
          input → predict:{' '}
          <span style={{ color: timingColor(breakdown.inputToPredictionMs) }}>
            {breakdown.inputToPredictionMs}ms
          </span>
        </div>
        <div>
          predict → confirm:{' '}
          <span style={{ color: timingColor(breakdown.predictionToServerConfirmMs) }}>
            {breakdown.predictionToServerConfirmMs}ms
          </span>
        </div>
        {breakdown.confirmToVfxMs !== null && (
          <div>
            confirm → VFX:{' '}
            <span style={{ color: timingColor(breakdown.confirmToVfxMs) }}>
              {breakdown.confirmToVfxMs}ms
            </span>
          </div>
        )}
        <div>
          confirm → FCT:{' '}
          <span style={{ color: timingColor(breakdown.confirmToFctMs) }}>
            {breakdown.confirmToFctMs}ms
          </span>
        </div>
        <div style={{ marginTop: 4, borderTop: '1px solid #333', paddingTop: 4 }}>
          total:{' '}
          <span style={{ color: timingColor(breakdown.totalMs), fontWeight: 'bold' }}>
            {breakdown.totalMs}ms
          </span>
        </div>
      </div>
      <div style={{ marginTop: 6, fontSize: 9, color: '#555', fontStyle: 'italic' }}>
        Green &lt;50ms · Yellow 50-150ms · Red &gt;150ms
      </div>
    </div>
  );
};

export function formatAttributionString(breakdown: CastTimingBreakdown, abilityName?: string): string {
  const header = abilityName ? `${abilityName} — ` : '';
  const parts: string[] = [
    `input ${breakdown.inputToPredictionMs}ms`,
    `server ${breakdown.predictionToServerConfirmMs}ms`,
    ...(breakdown.confirmToVfxMs !== null ? [`VFX ${breakdown.confirmToVfxMs}ms`] : []),
    `FCT ${breakdown.confirmToFctMs}ms`,
    `total ${breakdown.totalMs}ms`,
  ];
  return `${header}${parts.join(' · ')}`;
}
